import { useEffect, useRef } from 'react';

import { useAnalytics } from '@townsquare/analytics';
import { useCollabContext } from '@townsquare/collab-context';
import { setFavicon } from '@townsquare/dynamic-favicon';
import {
	useRovoAppification,
	useIsEligibleForRovoAppification,
} from '@townsquare/rovo/appification';
import { useRovoEntitlement } from '@townsquare/rovo/entitlement';
import { useRovoHomeBranding } from '@townsquare/rovo/home-branding';
import { manuallyFireExperimentExposure } from '@townsquare/stat-sig';
import { TOWNSQUARE_ENABLE_ROVO_HOME_APPIFICATION_EXP } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';

export const RovoAppificationHandler = () => {
	const { rovoAppificationState } = useRovoAppification();
	const { isEligibleForAppification } = useIsEligibleForRovoAppification();
	const rovoHomeBranding = useRovoHomeBranding();
	const rovoEntitlement = useRovoEntitlement();
	const analytics = useAnalytics();
	const hasFiredRef = useRef(false);
	const collabContext = useCollabContext();

	const hasTownsquare = !!collabContext?.goalWorkspace || !!collabContext?.projectWorkspace;
	const hasMultipleUnits = collabContext?.collabContextScoping?.hasMultipleUnits;
	const collabContextCloudIds =
		collabContext?.collabContextScoping?.collabContextCloudIds?.length ?? 0;
	const collabContextSiteAris =
		collabContext?.collabContextScoping?.collabContextSiteAris?.length ?? 0;

	const collabContextLoaded = !!collabContext;

	useEffect(() => {
		if (!collabContextLoaded) {
			return;
		}

		if (hasFiredRef.current) {
			return;
		}

		if (rovoAppificationState !== 'loading' && !rovoEntitlement.loading && rovoEntitlement.data) {
			const homeAppType = rovoAppificationState === 'enabled' ? 'rovo' : 'classic';

			void analytics.setTopLevelAttributes({
				homeAppType,
			});

			void analytics.operational('homeApp', 'loaded', {
				isRovoEnabled: rovoEntitlement.data.isRovoEnabled,
				isRovoLLMEnabled: rovoEntitlement.data.isRovoLLMEnabled,
				rovoAppification: rovoAppificationState === 'enabled',
				hasTownsquare,
				hasMultipleUnits,
				collabContextCloudIds,
				collabContextSiteAris,
			});

			hasFiredRef.current = true;

			// explicitly fire experiment exposure for eligible users in experiment cohorts
			if (
				fg('townsquare_enable_rovo_appification_v2') &&
				isEligibleForAppification === 'eligible'
			) {
				manuallyFireExperimentExposure(TOWNSQUARE_ENABLE_ROVO_HOME_APPIFICATION_EXP);
			}

			setFavicon(rovoHomeBranding?.icon ?? 'home');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		collabContextLoaded,
		hasTownsquare,
		hasMultipleUnits,
		collabContextCloudIds,
		collabContextSiteAris,
		rovoAppificationState,
		rovoHomeBranding?.icon,
		rovoEntitlement.loading,
		rovoEntitlement.data?.isRovoEnabled,
		rovoEntitlement.data?.isRovoLLMEnabled,
	]);

	return null;
};
